import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Check, RotateCcw, Plus, X, Loader2, Target, Zap, Clock, Mail, ArrowDownToLine, Settings as SettingsIcon, Bell, BellOff, KeyRound, ChevronDown, ChevronUp } from "lucide-react";
import { LOGO_DARK_URL } from "@/lib/checkout";
import { MobileTabBar } from "@/components/MobileTabBar";
import LowBatteryMode, { BrainCheckIn } from "@/components/LowBatteryMode";
import { trackResetStart, trackResetComplete } from "@/lib/analytics";
import { isNative, authHeaders, setAuthToken, loadAuthToken, hapticTap, initNativeShell, kvGet, kvSet, kvRemove, apiBase } from "@/lib/native";
import { syncBlockReminders, getBlockReminderSet, toggleBlockReminder, checkPermission, requestPermission } from "@/lib/reminders";

const API = `${apiBase()}/api`;

const api = async (path, options = {}) => {
  const extra = await authHeaders();
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...extra },
    ...options,
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
};

const dstr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayStr = () => dstr(new Date());
const yesterdayStr = () => { const d = new Date(); d.setDate(d.getDate() - 1); return dstr(d); };
const prettyDate = () => new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PENDING_KEY = () => `cc_today_pending_${todayStr()}`;
const isBlank = (e) => !(e.priority || "").trim() && (e.quick_wins || []).every((w) => !(w.text || "").trim());

export default function Today() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [entry, setEntry] = useState(null);
  const [save, setSave] = useState("idle"); // idle | saving | saved | device
  const [notice, setNotice] = useState("");
  const [carry, setCarry] = useState(null); // array of unfinished wins from yesterday
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [codeErr, setCodeErr] = useState("");
  const [blockRem, setBlockRem] = useState({});
  const [blocksOpen, setBlocksOpen] = useState(true);
  useEffect(() => { setBlocksOpen((entry?.brain_state || "") !== "struggling"); }, [entry?.brain_state]);
  const timer = useRef(null);

  const persist = useCallback(async (next) => {
    const payload = {
      date: todayStr(),
      priority: next.priority,
      quick_wins: next.quick_wins,
      time_blocks: next.time_blocks,
      client_updated_at: next.updated_at || null,
    };
    setSave("saving");
    try {
      const saved = await api("/today", { method: "PUT", body: JSON.stringify(payload) });
      await kvRemove(PENDING_KEY());
      setEntry((prev) => (prev ? { ...prev, updated_at: saved.updated_at } : prev));
      setSave("saved");
      setTimeout(() => setSave((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (e) {
      if (String(e.message) === "409") {
        // Server has newer data (edited elsewhere): drop our stale write and refetch.
        await kvRemove(PENDING_KEY());
        try { setEntry(await api(`/today?date=${todayStr()}`)); } catch (e3) { /* */ }
        setSave("idle");
        return;
      }
      // Offline / network hiccup: keep the work locally and retry on reconnect.
      await kvSet(PENDING_KEY(), JSON.stringify(payload));
      setSave("device");
    }
  }, []);

  const flushPending = useCallback(async () => {
    let raw = null;
    try { raw = await kvGet(PENDING_KEY()); } catch (e) { return; }
    if (!raw) return;
    try {
      const saved = await api("/today", { method: "PUT", body: raw });
      await kvRemove(PENDING_KEY());
      setEntry((prev) => (prev ? { ...prev, updated_at: saved.updated_at } : prev));
      setSave("saved");
      setTimeout(() => setSave((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (e) {
      if (String(e.message) === "409") {
        // Newer server data wins: discard the stale queued write and refetch.
        await kvRemove(PENDING_KEY());
        try { setEntry(await api(`/today?date=${todayStr()}`)); } catch (e3) { /* */ }
      }
      /* else stay queued */
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("login") === "ok") setNotice("You are signed in.");
    if (params.get("login") === "failed") setNotice("That link expired or was already used. Request a fresh one.");
    if (params.get("login")) window.history.replaceState(null, "", "/today");
    let cleanupShell = () => {};
    (async () => {
      await loadAuthToken();
      let u = null;
      try { u = await api("/community/auth/me"); } catch (e) { u = null; }
      setUser(u);
    })();
    initNativeShell().then((fn) => { if (fn) cleanupShell = fn; });
    const onOnline = () => flushPending();
    window.addEventListener("online", onOnline);
    return () => { window.removeEventListener("online", onOnline); cleanupShell(); };
  }, [flushPending]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      await flushPending();
      let d = null;
      try { d = await api(`/today?date=${todayStr()}`); } catch (e) { return; }
      setEntry(d);
      if (isNative()) {
        getBlockReminderSet(todayStr()).then(setBlockRem);
        syncBlockReminders(todayStr(), d.time_blocks || []);
      }
      if (isBlank(d) && !d.carryover_resolved) {
        try {
          const y = await api(`/today?date=${yesterdayStr()}`);
          const unfinished = (y.quick_wins || []).filter((w) => (w.text || "").trim() && !w.done).slice(0, 3);
          if (unfinished.length) setCarry(unfinished);
        } catch (e) { /* */ }
      }
    })();
  }, [user, flushPending]);

  const update = (patch) => {
    setEntry((prev) => {
      const next = { ...prev, ...patch };
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => persist(next), 700);
      return next;
    });
  };
  const setQuickWin = (i, patch) => update({ quick_wins: entry.quick_wins.map((w, idx) => (idx === i ? { ...w, ...patch } : w)) });
  const addBlock = () => update({ time_blocks: [...(entry.time_blocks || []), { time: "", label: "" }] });
  const setBlock = (i, patch) => update({ time_blocks: entry.time_blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) });
  const removeBlock = (i) => update({ time_blocks: entry.time_blocks.filter((_, idx) => idx !== i) });

  // Keep native time-block reminders in sync whenever the blocks change.
  useEffect(() => {
    if (!isNative() || !entry) return;
    syncBlockReminders(todayStr(), entry.time_blocks || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry && JSON.stringify(entry.time_blocks)]);

  const toggleBlockRem = async (i) => {
    const on = !blockRem[i];
    if (on) {
      let perm = await checkPermission();
      if (perm !== "granted") perm = await requestPermission();
      if (perm !== "granted") {
        setNotice("Turn on notifications in Settings to get time-block reminders.");
        return;
      }
    }
    const set = await toggleBlockReminder(todayStr(), i, on, entry.time_blocks || []);
    setBlockRem({ ...set });
  };

  const acceptCarry = () => {
    if (!carry) return;
    const chosen = carry;
    const wins = chosen.map((w) => ({ text: w.text, done: false }));
    while (wins.length < 3) wins.push({ text: "", done: false });
    setCarry(null);
    setNotice(`Brought over ${chosen.length} quick win${chosen.length === 1 ? "" : "s"} from yesterday.`);
    api("/today/carryover-resolve", { method: "POST", body: JSON.stringify({ date: todayStr() }) }).catch(() => {});
    update({ quick_wins: wins });
  };

  const startFresh = () => {
    setCarry(null);
    api("/today/carryover-resolve", { method: "POST", body: JSON.stringify({ date: todayStr() }) }).catch(() => {});
  };

  const resetDay = async () => {
    if (timer.current) clearTimeout(timer.current);
    try {
      const d = await api("/today/reset", { method: "POST", body: JSON.stringify({ date: todayStr() }) });
      setEntry(d);
      if (isNative()) syncBlockReminders(todayStr(), d.time_blocks || []);
      setNotice(`Fresh start. Reset ${d.reset_count} time${d.reset_count === 1 ? "" : "s"} today, and that is completely fine.`);
    } catch (e) { /* */ }
  };

  const setBrainState = async (value) => {
    hapticTap();
    if (value === "overwhelmed") trackResetStart({ source: "brain_checkin" });
    setEntry((prev) => (prev ? { ...prev, brain_state: value } : prev));
    try {
      await api("/today/brain-state", { method: "POST", body: JSON.stringify({ date: todayStr(), brain_state: value }) });
    } catch (e) { /* offline: keep the local choice */ }
  };

  const requestLink = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) return;
    try {
      await api("/community/auth/request", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), origin_url: window.location.origin, next: "/today" }),
      });
    } catch (e2) { /* generic */ }
    setLinkSent(true);
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!/^[0-9]{6}$/.test(code.trim())) { setCodeErr("Enter the 6-digit code from your email."); return; }
    setVerifying(true);
    setCodeErr("");
    try {
      const res = await api("/community/auth/verify-code", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      if (res.token) await setAuthToken(res.token);
      const u = await api("/community/auth/me");
      setUser(u);
    } catch (err) {
      setCodeErr(String(err.message) === "403"
        ? "That email does not have Command Center access yet."
        : "That code is invalid or expired. Request a fresh one.");
      setVerifying(false);
    }
  };

  const rootStyle = { overscrollBehavior: "none" };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F6F8FA]" style={rootStyle} data-testid="today-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#15816B]" aria-hidden="true" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col bg-[#F6F8FA] px-5 py-8 font-body text-[#0B132B]" style={rootStyle} data-testid="today-login">
        <Link to="/" className="mx-auto mb-8 inline-flex" data-testid="today-brand">
          <img src={LOGO_DARK_URL} alt="CommandCenterADHD" width="1085" height="537" className="h-10 w-auto object-contain" />
        </Link>
        <div className="mx-auto w-full max-w-sm rounded-2xl border border-[#E6E9EE] bg-white p-6 shadow-[0_10px_30px_-18px_rgba(11,19,43,0.4)]">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Your day, one screen</h1>
          <p className="mt-2 text-base text-[#5A6472]">
            Sign in with the email you used to buy the Command Center. We send a one time login link, no password.
          </p>
          {linkSent ? (
            <div className="mt-6 space-y-4" data-testid="today-link-sent">
              <div className="rounded-xl bg-[#EAF5F1] p-4 text-sm text-[#15816B]">
                {isNative()
                  ? "If that email owns the Command Center, a login link and a 6-digit code are on their way. Enter the code below to sign in."
                  : "If that email owns the Command Center, a login link is on its way. Open it on this device to land right back here on Today."}
              </div>
              {isNative() && (
                <form onSubmit={verifyCode} className="space-y-3" data-testid="today-code-form">
                  <label className="block text-sm font-semibold" htmlFor="today-code">6-digit code</label>
                  <input
                    id="today-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    data-testid="today-code-input"
                    className="w-full rounded-xl border border-[#D9DEE6] bg-white px-4 py-3 text-center text-xl tracking-[0.4em] outline-none focus:border-[#15816B] focus:ring-2 focus:ring-[#15816B]/20"
                  />
                  {codeErr && <p className="text-sm text-[#B4443E]" data-testid="today-code-error">{codeErr}</p>}
                  <button
                    type="submit"
                    disabled={verifying}
                    data-testid="today-code-verify"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B132B] py-3 font-heading text-base font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {verifying ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <KeyRound className="h-4 w-4" aria-hidden="true" />}
                    Verify and sign in
                  </button>
                </form>
              )}
            </div>
          ) : (
            <form onSubmit={requestLink} className="mt-6 space-y-3" data-testid="today-login-form">
              <label className="block text-sm font-semibold" htmlFor="today-email">Your email</label>
              <input
                id="today-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@email.com"
                data-testid="today-email-input"
                className="w-full rounded-xl border border-[#D9DEE6] bg-white px-4 py-3 text-base outline-none focus:border-[#15816B] focus:ring-2 focus:ring-[#15816B]/20"
              />
              <button
                type="submit"
                data-testid="today-login-button"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B132B] py-3 font-heading text-base font-bold text-white transition-transform duration-200 hover:-translate-y-0.5"
              >
                <Mail className="h-4 w-4" aria-hidden="true" /> Send my login link
              </button>
            </form>
          )}
          {notice && <p className="mt-4 text-sm text-[#5A6472]" data-testid="today-notice">{notice}</p>}
          {!isNative() && (
            <p className="mt-6 text-center text-xs text-[#8A94A6]">
              Don&apos;t have access yet?{" "}
              <a href="/get-started" className="font-semibold text-[#15816B] underline-offset-2 hover:underline">
                Get the Command Center for $17
              </a>
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F6F8FA]" style={rootStyle}>
        <Loader2 className="h-6 w-6 animate-spin text-[#15816B]" aria-hidden="true" />
      </div>
    );
  }

  const doneCount = entry.quick_wins.filter((w) => w.done && w.text.trim()).length;
  const brainState = entry.brain_state || "";

  return (
    <div className="min-h-screen bg-[#F6F8FA] font-body text-[#0B132B]" style={rootStyle} data-testid="today-page">
      <header
        className="sticky top-0 z-20 border-b border-[#12203c] bg-[#0B132B] px-5 py-4 text-white"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 1rem)" }}
      >
        <div className="mx-auto flex max-w-md items-center justify-between">
          <div>
            <p className="font-heading text-lg font-extrabold leading-none">Today</p>
            <p className="mt-1 text-xs text-[#9FB2C9]" data-testid="today-date">{prettyDate()}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#9FB2C9]" data-testid="today-save-state">
              {save === "saving" ? "Saving" : save === "saved" ? "Saved" : save === "device" ? "Saved on device" : ""}
            </span>
            <Link
              to="/settings"
              data-testid="today-settings"
              aria-label="Settings"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 transition-colors hover:bg-white/20"
            >
              <SettingsIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-md space-y-5 px-5 pb-28 pt-6">
        {notice && (
          <div className="rounded-xl bg-[#EAF5F1] px-4 py-3 text-sm text-[#15816B]" data-testid="today-inline-notice">
            {notice}
          </div>
        )}

        {carry && (
          <div className="rounded-2xl border border-[#F0D9A8] bg-[#FDF7EA] p-4" data-testid="today-carryover">
            <div className="flex items-start gap-2">
              <ArrowDownToLine className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B8860B]" aria-hidden="true" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#7a5a10]">
                  Bring over {carry.length} unfinished quick win{carry.length === 1 ? "" : "s"} from yesterday?
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={acceptCarry}
                    data-testid="today-carryover-yes"
                    className="rounded-lg bg-[#0B132B] px-3.5 py-2 text-sm font-bold text-white transition-transform hover:-translate-y-0.5"
                  >
                    Yes, bring them over
                  </button>
                  <button
                    type="button"
                    onClick={startFresh}
                    data-testid="today-carryover-fresh"
                    className="rounded-lg border border-[#E0CFA0] bg-white px-3.5 py-2 text-sm font-semibold text-[#7a5a10] transition-colors hover:bg-[#FBF3E0]"
                  >
                    Start fresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <BrainCheckIn value={brainState} onSelect={setBrainState} />

        {brainState === "overwhelmed" ? (
          <LowBatteryMode
            entry={entry}
            onPriorityChange={(v) => update({ priority: v })}
            onToggleWin={(i) => { hapticTap(); setQuickWin(i, { done: !entry.quick_wins[i].done }); }}
            onWinTextChange={(i, text) => setQuickWin(i, { text })}
            onExit={() => setBrainState("ready")}
            onResetDay={resetDay}
            onHaptic={hapticTap}
            onCompleteAction={(payload) => trackResetComplete(payload)}
          />
        ) : (
        <>
        <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="today-priority">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-[#15816B]" aria-hidden="true" />
            <h2 className="font-heading text-base font-extrabold">The one thing today</h2>
          </div>
          <p className="mt-1 text-xs text-[#8A94A6]">Pick one priority worth protecting time for.</p>
          <textarea
            value={entry.priority}
            onChange={(e) => update({ priority: e.target.value })}
            rows={2}
            maxLength={280}
            placeholder="What matters most today?"
            data-testid="today-priority-input"
            className="mt-3 w-full resize-none rounded-xl border border-[#D9DEE6] bg-[#FAFBFC] px-4 py-3 text-base leading-snug outline-none focus:border-[#15816B] focus:ring-2 focus:ring-[#15816B]/20"
          />
        </section>

        <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="today-quick-wins">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-[#F5A623]" aria-hidden="true" />
              <h2 className="font-heading text-base font-extrabold">Quick wins</h2>
            </div>
            <span className="text-xs font-semibold text-[#8A94A6]" data-testid="today-quick-wins-count">{doneCount}/3</span>
          </div>
          <div className="mt-3 space-y-2.5">
            {entry.quick_wins.map((w, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`today-quick-win-${i}`}>
                <button
                  type="button"
                  onClick={() => { hapticTap(); setQuickWin(i, { done: !w.done }); }}
                  aria-label={w.done ? "Mark not done" : "Mark done"}
                  data-testid={`today-quick-win-check-${i}`}
                  className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border transition-colors ${
                    w.done ? "border-[#15816B] bg-[#15816B] text-white" : "border-[#CBD3DE] bg-white text-transparent"
                  }`}
                >
                  <Check className="h-4 w-4" aria-hidden="true" />
                </button>
                <input
                  type="text"
                  value={w.text}
                  onChange={(e) => setQuickWin(i, { text: e.target.value })}
                  maxLength={140}
                  placeholder={`Quick win ${i + 1}`}
                  data-testid={`today-quick-win-input-${i}`}
                  className={`w-full border-b border-transparent bg-transparent py-1.5 text-base outline-none focus:border-[#15816B] ${
                    w.done ? "text-[#8A94A6] line-through" : "text-[#0B132B]"
                  }`}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="today-time-blocks">
          <button
            type="button"
            onClick={() => setBlocksOpen((v) => !v)}
            data-testid="today-time-blocks-toggle"
            className="flex w-full items-center justify-between text-left"
          >
            <span className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#3A7CA5]" aria-hidden="true" />
              <span className="font-heading text-base font-extrabold">Honest time blocks</span>
            </span>
            {blocksOpen ? <ChevronUp className="h-4 w-4 text-[#8A94A6]" aria-hidden="true" /> : <ChevronDown className="h-4 w-4 text-[#8A94A6]" aria-hidden="true" />}
          </button>
          {blocksOpen && (
          <>
          <p className="mt-1 text-xs text-[#8A94A6]">A loose shape for the day, not a rigid schedule.</p>
          <div className="mt-3 space-y-2.5">
            {(entry.time_blocks || []).length === 0 && (
              <p className="text-sm text-[#B4BCC8]" data-testid="today-time-blocks-empty">No blocks yet. Add one when you know roughly when something fits.</p>
            )}
            {(entry.time_blocks || []).map((b, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`today-time-block-${i}`}>
                <input
                  type="text"
                  value={b.time}
                  onChange={(e) => setBlock(i, { time: e.target.value })}
                  maxLength={16}
                  placeholder="9am"
                  data-testid={`today-time-block-time-${i}`}
                  className="w-20 flex-shrink-0 rounded-lg border border-[#D9DEE6] bg-[#FAFBFC] px-2.5 py-2.5 text-base outline-none focus:border-[#3A7CA5]"
                />
                <input
                  type="text"
                  value={b.label}
                  onChange={(e) => setBlock(i, { label: e.target.value })}
                  maxLength={140}
                  placeholder="What are you doing?"
                  data-testid={`today-time-block-label-${i}`}
                  className="w-full rounded-lg border border-[#D9DEE6] bg-[#FAFBFC] px-3 py-2.5 text-base outline-none focus:border-[#3A7CA5]"
                />
                {isNative() && (
                  <button
                    type="button"
                    onClick={() => toggleBlockRem(i)}
                    aria-label={blockRem[i] ? "Turn off reminder" : "Remind me 5 minutes before"}
                    data-testid={`today-time-block-remind-${i}`}
                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
                      blockRem[i] ? "bg-[#EAF5F1] text-[#15816B]" : "text-[#8A94A6] hover:bg-[#F1F4F8]"
                    }`}
                  >
                    {blockRem[i] ? <Bell className="h-4 w-4" aria-hidden="true" /> : <BellOff className="h-4 w-4" aria-hidden="true" />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeBlock(i)}
                  aria-label="Remove block"
                  data-testid={`today-time-block-remove-${i}`}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-[#8A94A6] transition-colors hover:bg-[#F1F4F8] hover:text-[#B4443E]"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addBlock}
              data-testid="today-add-block"
              className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#15816B] transition-colors hover:text-[#0B6b57]"
            >
              <Plus className="h-4 w-4" aria-hidden="true" /> Add a time block
            </button>
          </div>
          </>
          )}
        </section>

        <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="today-reset-section">
          <p className="text-sm text-[#5A6472]">Day went sideways? Start clean, no shame.</p>
          <button
            type="button"
            onClick={resetDay}
            data-testid="today-reset-button"
            className="mt-3 inline-flex items-center gap-2 rounded-xl border border-[#D9DEE6] bg-white px-4 py-2.5 font-heading text-sm font-bold text-[#0B132B] transition-colors hover:bg-[#F1F4F8]"
          >
            <RotateCcw className="h-4 w-4 text-[#15816B]" aria-hidden="true" /> Reset the day
          </button>
        </section>
        </>
        )}
      </main>

      <MobileTabBar active="today" />
    </div>
  );
}
